/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ComponentSeries,
  ComponentType,
  ComponentWithEntriesCheck,
  ExamDate,
  Roles,
} from '@cambridgeassessment/lib-ee-commons';
import SupportingMaterialComponent from './SupportingMaterialFC';
import * as thunks from '../../../../store/components/thunks';
import { LocalSection } from '../../../../service/components/types';
import {
  StyledComponentHeader,
  StyledExpansionPanel,
  StyledExpansionPanelDetails,
  StyledExpansionSummary,
  StyledComponentHeaderText,
  StyledComponentHeaderContainer,
  StyledReadOnlyComponent,
  StyledLink,
  StyledComponentHeaderAndLinkContainer,
  StyledModerationLabelContainer,
  MarkLabel,
  StyledParagraph,
  StyledEntriesDisclaimer,
  StyledHorizontalLine,
  StyledConfirmButton,
  StyledPoeChip,
} from './styles';
import ExamDatePerZoneSelector from './ExamDatePerZoneSelector';
import DigitallyReceivedControl from './DigitallyReceivedControl';
import { Link } from 'react-router-dom';
import Sections from './Sections/Sections';
import ComponentDocuments from './ComponentDocuments/ComponentDocuments';
import { Chip } from '@cambridgeassessment/cambridge-ui';
import ComponentRules from './ComponentRules';
import actions from '../../../../store/components/actions';
import { getSeriesData } from '../../../../store/series/thunks';
import { RootState } from '../../../../store';
import { Authorized } from 'lib-browser';
import { ENTRIES_RECEIVED_ACTIVE_SERIES_TEXT } from '../../../../constants';

interface Props {
  component: ComponentWithEntriesCheck;
  index: number;
  disabled?: boolean;
  hideSections?: boolean;
  fullTitle?: boolean;
}

const ComponentFC: React.FC<Props> = ({ component, disabled, hideSections, fullTitle }) => {
  let compId: string;
  if (component._id != null) {
    compId = component._id;
  } else {
    throw Error('Missing component _id');
  }

  const {
    series: { seriesData },
    userData: { selectedRole },
  } = useSelector((state: RootState) => state);
  const dispatch = useDispatch();
  const sectionsCount = component?.sections?.length || 0;
  const isExam = component.isExam;
  const [removeEnabled, setRemoveEnabled] = useState<boolean>(false);
  const [localSections, setLocalSections] = useState<LocalSection[]>([]);

  const deleteLocalSection = (): void => setLocalSections([]);

  const isComponentModerated = component.type === ComponentType.MODERATED;
  const withSections = !!(component.sections && component.sections.length);

  const addOrUpdateSeriesExamDates = (series: ComponentSeries, examDate: ExamDate): void => {
    const examDatesByZoneId = series.examDates.find((date: ExamDate) => date.zoneId === examDate.zoneId);
    const noSubmissionOffsetDates = series.examDates.filter((date) => !!date && !date.submissionOffset);
    const examsWithSubmissionOffsetDates = series.examDates.filter((date) => date && date.submissionOffset);
    const examsToUpdate: ExamDate[] = [];

    for (const item of noSubmissionOffsetDates) {
      examsToUpdate.push({ ...item, isExam: true, submissionOffset: { date: 5, buffer: 14 } });
    }

    if (examDatesByZoneId) {
      const datesToReturn = series.examDates.filter((date) => date.zoneId !== examDate.zoneId && date.submissionOffset);
      const updatedExamDates = [...datesToReturn, ...examsToUpdate, examDate];
      dispatch(thunks.updateSeries(compId, updatedExamDates, 'examDates', series.id));
    } else {
      dispatch(
        thunks.updateSeries(
          compId,
          [...examsToUpdate, ...examsWithSubmissionOffsetDates, examDate],
          'examDates',
          series.id,
        ),
      );
    }
  };

  const isDigitalReceivedChecked = isComponentModerated ? component.receiveSamples : withSections;
  const allowComponentConfiguration = isComponentModerated
    ? component.receiveMarks || component.receiveSamples
    : withSections;

  useEffect(() => {
    const isRemoveEnabled = sectionsCount > 1;
    setRemoveEnabled(isRemoveEnabled);
  }, [component, sectionsCount]);

  const updateToBeConfirmed = () => {
    dispatch(actions.setIsLoading(true));
    dispatch(thunks.updateToBeConfirmed(component._id, false));
  };

  const resolveLinkRender = (): JSX.Element => {
    const linkQaId = !withSections
      ? `configure-component-${component.number}-state-readOnly`
      : `configure-component-${component.number}-state`;

    return (
      <StyledReadOnlyComponent readOnly={!allowComponentConfiguration} data-qa-id={linkQaId}>
        <StyledLink>
          {component.toBeConfirmed ? (
            <Link
              data-qa-id={`configure-component-${component.number}`}
              to={`/components/review-configure-component/${component._id}`}
            >
              {component.toBeConfirmed ? 'Review component configuration' : 'Configure component'}
            </Link>
          ) : (
            <Link
              data-qa-id={`configure-component-${component.number}`}
              to={`/components/configure-component/${component._id}`}
            >
              {component.toBeConfirmed ? 'Review component configuration' : 'Configure component'}
            </Link>
          )}
        </StyledLink>
      </StyledReadOnlyComponent>
    );
  };

  const moderatedComponentLabels = (): JSX.Element => (
    <StyledModerationLabelContainer>
      <Chip value={'Moderated'} dataQaId={`moderated-component-badge-${component.number}`} />
      <MarkLabel data-qa-id={`moderated-component-max-mark-label-${component.number}`}>
        Max mark value:
        <span data-qa-id={`max-component-mark-${component.number}`}>{component.maxMark || 0}</span>
      </MarkLabel>
    </StyledModerationLabelContainer>
  );

  const dropdownContent = (): JSX.Element => {
    return (
      <Authorized
        selectedRole={selectedRole}
        roles={[Roles.SUBMIT_SUPER_ADMIN, Roles.SUBMIT_ADMINISTRATOR]}
        readOnlyRoles={[Roles.SUBMIT_COMPLIANCE, Roles.SUBMIT_INTERNAL_ASSESSMENT_MANAGER]}
      >
        {isExam && !isComponentModerated && seriesData && (
          <ExamDatePerZoneSelector
            addOrUpdateSeriesExamDate={addOrUpdateSeriesExamDates}
            component={component}
            disabled={disabled}
            seriesData={seriesData}
          />
        )}
        {isComponentModerated && moderatedComponentLabels()}

        <ComponentRules component={component} disabled={disabled} />

        {component.allowAdditionalWorkSubmission && (
          <ComponentDocuments component={component} componentId={compId} removeEnabled={true} disabled={disabled} />
        )}
        {isDigitalReceivedChecked && (
          <>
            <Sections
              component={component}
              componentId={compId}
              localSections={localSections}
              removeEnabled={removeEnabled}
              setLocalSections={(sections) => setLocalSections(sections)}
              disabled={disabled}
              withSections={withSections}
              readOnly={disabled}
            />
            <StyledExpansionPanelDetails>
              <StyledComponentHeaderContainer>
                <StyledComponentHeaderText>Supporting Material</StyledComponentHeaderText>
              </StyledComponentHeaderContainer>
              <StyledParagraph>Additional candidate work that can be optionally submitted.</StyledParagraph>
              <SupportingMaterialComponent component={component} disabled={disabled} />
            </StyledExpansionPanelDetails>
          </>
        )}
        {component.toBeConfirmed && (
          <div>
            <StyledHorizontalLine />
            <Authorized
              selectedRole={selectedRole}
              roles={[Roles.SUBMIT_SUPER_ADMIN, Roles.SUBMIT_ADMINISTRATOR]}
              readOnlyRoles={[Roles.SUBMIT_COMPLIANCE, Roles.SUBMIT_INTERNAL_ASSESSMENT_MANAGER]}
            >
              <StyledConfirmButton qaId='confirm-configurations-button' onClick={() => updateToBeConfirmed()}>
                Confirm copied configurations
              </StyledConfirmButton>
            </Authorized>
          </div>
        )}
      </Authorized>
    );
  };

  const compNameStr = fullTitle
    ? `${component.assessment.name} - ${component.name}`
    : `Component ${component.number} - ${component.name}`;

  useEffect(() => {
    if (!seriesData) {
      dispatch(getSeriesData(component.businessUnit.id));
    }
  }, [component.businessUnit.id, dispatch, seriesData]);

  useEffect(() => {
    if (!seriesData) {
      dispatch(getSeriesData(component.businessUnit.id));
    }
  }, [component.businessUnit.id, dispatch, seriesData]);

  useEffect(() => {
    if (!seriesData) {
      dispatch(getSeriesData(component.businessUnit.id));
    }
  }, [component.businessUnit.id, dispatch, seriesData]);

  return (
    <StyledComponentHeaderAndLinkContainer isShowBorder={!!hideSections}>
      <StyledExpansionPanel key={`${component.name}-${component.number}`} expanded={allowComponentConfiguration}>
        <StyledExpansionSummary
          tabIndex={-1}
          readOnly={disabled}
          aria-controls='panel1a-content'
          data-qa-id={`expansion-summary-header-${component.name}-${component.number}`}
          expandIcon={
            <DigitallyReceivedControl
              compId={compId}
              component={component}
              disabled={disabled}
              switchChecked={isDigitalReceivedChecked}
              deleteLocalSection={deleteLocalSection}
            />
          }
          IconButtonProps={{ disableRipple: true }}
        >
          <StyledComponentHeader data-qa-id={`${component.name}-${component.number}-header`}>
            <div>
              {compNameStr} {component.toBeConfirmed && '(not confirmed)'}
            </div>
            <StyledPoeChip>
              {component.isPoE && (
                <Chip value={'Portfolio of evidence'} dataQaId={`poe-component-badge-${component.number}`} />
              )}
            </StyledPoeChip>
          </StyledComponentHeader>
        </StyledExpansionSummary>
        {!hideSections && dropdownContent()}
      </StyledExpansionPanel>
      {isComponentModerated && hideSections && moderatedComponentLabels()}
      {hideSections && resolveLinkRender()}
      {hideSections && component.hasEntries && (
        <StyledEntriesDisclaimer data-qa-id={`${component.name}-entries-disclaimer`}>
          {ENTRIES_RECEIVED_ACTIVE_SERIES_TEXT}
        </StyledEntriesDisclaimer>
      )}
    </StyledComponentHeaderAndLinkContainer>
  );
};

export default ComponentFC;
